import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/services/api";
import Loader from "@/components/loader";
import toast from "react-hot-toast";
import { FaSort } from "react-icons/fa";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

const RoadmapColumn = ({ project, setDraggedItem, setSelectedItem, selectedItem, filters, refreshTrigger, callbacks }) => {
  const [roadlogItems, setRoadlogItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState({ field: "points", direction: "desc" });
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const sortButtonRef = useRef(null);
  const sortMenuRef = useRef(null);

  useEffect(() => {
    if (project?._id) {
      fetchRoadlogItems();
    }
  }, [project?._id, filters, refreshTrigger]);

  useEffect(() => {
    if (callbacks) {
      callbacks.onItemUpdate = (updatedItem) => {
        setRoadlogItems((prevItems) => prevItems.map((item) => (item._id === updatedItem._id ? updatedItem : item)));
      };

      callbacks.onItemDelete = (deletedItem) => {
        setRoadlogItems((prevItems) => prevItems.filter((item) => item._id !== deletedItem._id));
      };
    }

    return () => {
      if (callbacks) {
        callbacks.onItemUpdate = null;
        callbacks.onItemDelete = null;
      }
    };
  }, [callbacks]);

  // Close filter when ESC key is pressed
  useEffect(() => {
    if (!showFilters) return;

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setShowFilters(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showFilters]);

  // Added useEffect for click outside detection
  useEffect(() => {
    function handleClickOutside(event) {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target) && sortButtonRef.current && !sortButtonRef.current.contains(event.target)) {
        setIsSortMenuOpen(false);
      }
    }

    if (isSortMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortMenuOpen]);

  const addPoints = async (arr1) => {
    const arr = [...arr1];
    for (let i = 0; i < arr.length; i++) {
      let chargePoints = 0;
      if (arr[i].charge === "XL") chargePoints += 1;
      if (arr[i].charge === "L") chargePoints += 2;
      if (arr[i].charge === "M") chargePoints += 3;
      if (arr[i].charge === "S") chargePoints += 4;
      if (arr[i].charge === "XS") chargePoints += 5;

      let businessPoints = 0;
      if (arr[i].business_contribution === "XL") businessPoints += 5;
      if (arr[i].business_contribution === "L") businessPoints += 4;
      if (arr[i].business_contribution === "M") businessPoints += 3;
      if (arr[i].business_contribution === "S") businessPoints += 2;
      if (arr[i].business_contribution === "XS") businessPoints += 1;

      let basePoints = chargePoints * businessPoints;
      let urgentPoints = arr[i].urgent ? 100 : 0;
      arr[i].points = basePoints + urgentPoints;

      let days_required = 0;
      if (arr[i].charge === "XL") days_required += 20;
      if (arr[i].charge === "L") days_required += 15;
      if (arr[i].charge === "M") days_required += 10;
      if (arr[i].charge === "S") days_required += 5;
      if (arr[i].charge === "XS") days_required += 1;
      arr[i].days_required = days_required;
    }
    return arr;
  };

  const fetchRoadlogItems = async () => {
    if (!project?._id) return;
    setLoading(true);
    try {
      const { data, ok } = await api.post("/roadlog_item/search", {
        project_id: project._id,
        exclude_categories: ["BUG"],
        is_in_backlog: "no",
        ...filters,
      });

      if (!ok) return toast.error("Failed to fetch roadmap items");

      const itemsWithPoints = await addPoints(data);
      setRoadlogItems(itemsWithPoints);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch roadmap items");
    } finally {
      setLoading(false);
    }
  };

  const sortedItems = loading
    ? []
    : [...roadlogItems].sort((a, b) => {
        let comparison = 0;
        const field = sortBy.field;
        const direction = sortBy.direction === "asc" ? 1 : -1;

        if (field === "priority") {
          const priorityA = a.priority || "";
          const priorityB = b.priority || "";
          if (!priorityA && !priorityB) comparison = 0;
          if (!priorityA) return 1;
          if (!priorityB) return -1;
          comparison = priorityA.localeCompare(priorityB);
        }
        if (field === "createdAt") {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0); // Use epoch for null/undefined
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          comparison = dateA - dateB;
        }
        if (field === "updatedAt") {
          const dateA = a.updatedAt ? new Date(a.updatedAt) : new Date(0); // Use epoch for null/undefined
          const dateB = b.updatedAt ? new Date(b.updatedAt) : new Date(0);
          comparison = dateA - dateB;
        }
        if (field === "points") {
          const pointsA = typeof a.points === "number" ? a.points : 0;
          const pointsB = typeof b.points === "number" ? b.points : 0;

          if (pointsA === 0 && pointsB === 0) return 0;
          if (pointsA === 0) return 1;
          if (pointsB === 0) return -1;

          comparison = pointsA - pointsB;
        }
        return comparison * direction;
      });

  const handleSortChange = (field, direction) => {
    setSortBy({ field, direction });
    setIsSortMenuOpen(false);
  };

  // Define sortable fields for the menu
  const sortOptions = [
    { key: "priority", label: "Priority" },
    { key: "createdAt", label: "Created Date" },
    { key: "updatedAt", label: "Updated Date" },
    { key: "points", label: "Points" },
  ];

  // Helper function to determine item color based on status
  const getItemColor = (status) => {
    switch (status) {
      case "TODO":
        return "bg-blue-500";
      case "IN_PROGRESS":
        return "bg-yellow-500";
      case "NEED_INFO":
        return "bg-purple-500";
      case "DONE":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "TODO":
        return "bg-blue-50 text-blue-700";
      case "IN_PROGRESS":
        return "bg-yellow-50 text-yellow-700";
      case "NEED_INFO":
        return "bg-purple-50 text-purple-700";
      default:
        return "bg-green-50 text-green-700";
    }
  };

  const getStatusDotColor = (status) => {
    switch (status) {
      case "TODO":
        return "bg-blue-500";
      case "IN_PROGRESS":
        return "bg-yellow-500";
      case "NEED_INFO":
        return "bg-purple-500";
      default:
        return "bg-green-500";
    }
  };

  const handleItemClick = (item) => {
    const query = new URLSearchParams(search);
    query.set("item_modal_id", item._id);
    navigate({ search: query.toString() });

    setSelectedItem(selectedItem?._id === item._id ? null : item);
  };

  return (
    <div className="h-full transition-all duration-300 ease-in-out w-full flex flex-col">
      <div className="flex items-center justify-between mx-3 py-2 border-b border-gray-200">
        <h3 className="font-medium text-gray-700">Roadmap Items</h3>
        <div className="relative flex items-center gap-2" ref={sortButtonRef}>
          <button className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-0.5" onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}>
            <BiDotsVerticalRounded size={18} />
          </button>
          {isSortMenuOpen && (
            <div ref={sortMenuRef} className="absolute right-0 top-5 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
              <ul className="py-1">
                {sortOptions.map((option) => {
                  const isActiveField = sortBy.field === option.key;
                  const isAscActive = isActiveField && sortBy.direction === "asc";
                  const isDescActive = isActiveField && sortBy.direction === "desc";

                  return (
                    <li key={option.key} className={`flex justify-between items-center px-4 py-2 text-sm text-gray-700 ${isActiveField ? "font-semibold" : ""}`}>
                      <span>{option.label}</span>
                      <div className="flex items-center space-x-2 ml-2">
                        <span
                          className={`cursor-pointer p-1 rounded hover:bg-gray-200 ${isAscActive ? "text-blue-600 bg-gray-100" : "text-gray-400 hover:text-gray-600"}`}
                          onClick={() => handleSortChange(option.key, "asc")}>
                          <FaArrowUp size={12} />
                        </span>
                        <span
                          className={`cursor-pointer p-1 rounded hover:bg-gray-200 ${isDescActive ? "text-blue-600 bg-gray-100" : "text-gray-400 hover:text-gray-600"}`}
                          onClick={() => handleSortChange(option.key, "desc")}>
                          <FaArrowDown size={12} />
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-4">
          <Loader />
        </div>
      ) : (
        <div className="flex-1 gap-3 p-2 overflow-y-auto">
          {sortedItems.map((item) => (
            <div
              key={item._id}
              className={`bg-white p-2.5 rounded-lg border-l-2 border border-gray-100 shadow-sm hover:shadow transition-all duration-200 cursor-pointer w-full relative ${
                selectedItem?._id === item._id ? "border-purple-400 border-l-purple-400" : `border-l-${getItemColor(item.status)}`
              }`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("item", JSON.stringify(item));
                e.dataTransfer.setData("type", "roadlog_item");
                setDraggedItem(item);
              }}
              onDragEnd={() => setDraggedItem(null)}
              onClick={() => handleItemClick(item)}>
              <div className="flex justify-between items-center mb-1.5">
                <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-md px-1.5 py-0.5 ${getStatusColor(item.nnl)}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${getStatusDotColor(item.nnl)}`}></span>
                  {item.nnl?.replace("_", " ")}
                </span>
                <span className="text-xs font-mono text-gray-500">#{item.index}</span>
              </div>

              <div className="flex items-start gap-1.5 mb-2">
                <h3 className="text-gray-800 font-medium text-sm leading-tight flex-grow">{item.title}</h3>
                {item.urgent && (
                  <span className="inline-flex items-center gap-0.5 shrink-0 bg-red-50 text-red-700 text-xs px-1.5 py-0.5 rounded-full">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500"></span>
                    Urgent
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-1 items-center">
                {item.priority && (
                  <span
                    className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded border 
                    ${
                      item.priority === "NOW"
                        ? "bg-red-50 text-red-700 border-red-100"
                        : item.priority === "NEXT"
                        ? "bg-yellow-50 text-yellow-700 border-yellow-100"
                        : "bg-blue-50 text-blue-700 border-blue-100"
                    }`}>
                    {item.priority}
                  </span>
                )}
                {item.charge && (
                  <span className="inline-flex items-center bg-gray-50 text-gray-600 text-xs px-1.5 py-0.5 rounded border border-gray-100">
                    <span className="font-medium">C:</span>&nbsp;{item.charge}
                  </span>
                )}
                {item.business_contribution && (
                  <span className="inline-flex items-center bg-green-50 text-green-600 text-xs px-1.5 py-0.5 rounded border border-green-100">
                    <span className="font-medium">AB:</span>&nbsp;{item.business_contribution}
                  </span>
                )}
                {item.points !== undefined && item.points !== null && item.points !== 0 && (
                  <span className="inline-flex items-center bg-purple-50 text-purple-600 text-xs px-1.5 py-0.5 rounded border border-purple-100">
                    <span className="font-medium">PO:</span>&nbsp;{item.points}
                  </span>
                )}
                {item.module_name && (
                  <span className="inline-flex items-center bg-gray-50 text-gray-600 text-xs px-1.5 py-0.5 rounded border border-gray-100">{item.module_name}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RoadmapColumn;
